import { useState, useEffect, useRef, useMemo } from "react";
import type { UseMetadataFiltersReturn } from "../hooks/useMetadataFilters";
import {
  AUDIENCE_VALUES,
  EXTERNAL_REVIEW_VALUES,
  PROMOTION_TYPE_VALUES,
  NOC_STATUS_VALUES,
  TOPIC_VALUES,
  MIN_YEAR,
  MAX_YEAR,
} from "../hooks/useMetadataFilters";
import {
  APS_CATEGORY_OPTIONS,
  APS_SUBCATEGORY_OPTIONS,
} from "../config/filter-mappings";
import { useGraphQL } from "../hooks/useGraphQL";

// ========== Props ==========

interface MetadataFilterPanelProps {
  metadataFilters: UseMetadataFiltersReturn;
  onBack: () => void;
  onApply: () => void;
  excludedClientIds?: string[];
}

// ========== Sub-component Props ==========

interface FilterChipsProps {
  label: string;
  options: readonly string[];
  value: string | null;
  onChange: (val: string | null) => void;
}

interface SearchableFilterOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SearchableFilterProps {
  label: string;
  options: SearchableFilterOption[];
  values: string[];
  onChange: (vals: string[]) => void;
  placeholder?: string;
}

interface YearInputProps {
  label: string;
  value: number | null;
  onChange: (val: number | null) => void;
}

// ========== Sub-components ==========

function FilterChips({ label, options, value, onChange }: FilterChipsProps) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-sm font-medium text-gray-700 min-w-[120px] pt-1">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const isActive = value === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onChange(isActive ? null : option)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                isActive
                  ? "bg-maven-teal-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SearchableFilter({
  label,
  options,
  values,
  onChange,
  placeholder,
}: SearchableFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(lower));
  }, [options, search]);

  const toggleValue = (val: string) => {
    if (values.includes(val)) {
      onChange(values.filter((v) => v !== val));
    } else {
      onChange([...values, val]);
    }
  };

  const buttonLabel =
    values.length > 0
      ? `${label} (${values.length})`
      : placeholder || `Select ${label}...`;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
          if (open) setSearch("");
        }}
        className={`w-full px-3 py-2 text-sm text-left border rounded-lg flex items-center justify-between ${
          values.length > 0
            ? "border-maven-teal-500 text-maven-teal-700 dark:text-maven-teal-300 bg-maven-teal-50 dark:bg-maven-teal-500/15"
            : "border-gray-300 dark:border-maven-gray-800 text-gray-600 dark:text-maven-gray-300 bg-white dark:bg-maven-gray-800 hover:bg-gray-50 dark:hover:bg-maven-gray-950"
        }`}
      >
        <span className="truncate">{buttonLabel}</span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-maven-teal-500"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto px-1 pb-1">
            {filteredOptions.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">
                No results found
              </div>
            )}
            {filteredOptions.map((option) => {
              const isSelected = values.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={option.disabled}
                  onClick={() => {
                    if (!option.disabled) toggleValue(option.value);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-md text-left ${
                    option.disabled
                      ? "text-gray-400 cursor-not-allowed"
                      : isSelected
                        ? "bg-maven-teal-50 text-maven-teal-700"
                        : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <span
                    className={`flex-shrink-0 w-4 h-4 border rounded flex items-center justify-center ${
                      isSelected
                        ? "bg-maven-teal-500 border-maven-teal-500"
                        : "border-gray-300"
                    }`}
                  >
                    {isSelected && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </span>
                  <span className="truncate">
                    {option.label}
                    {option.disabled && (
                      <span className="text-gray-400 ml-1">(Restricted)</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function YearInput({ label, value, onChange }: YearInputProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === "") {
      onChange(null);
      return;
    }
    const num = parseInt(raw, 10);
    if (!isNaN(num) && num >= MIN_YEAR && num <= MAX_YEAR) {
      onChange(num);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <input
        type="number"
        min={MIN_YEAR}
        max={MAX_YEAR}
        value={value ?? ""}
        onChange={handleChange}
        placeholder="YYYY"
        className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-maven-teal-500"
      />
    </div>
  );
}

// ========== Dashboard Query ==========

// Asked of the Dashboard API as the signed-in person (graphql-proxy). PAAB
// Hasura dropped its `dashboard` source on 2026-10-07 (paab-hasura#14).
const GET_CLIENTS_QUERY = `
  query GetClients {
    clients {
      id
      name
      is_restricted
    }
  }
`;

type ClientsResponse = {
  clients: Array<{ id: string; name: string | null; is_restricted: boolean }>;
};

// ========== Main Component ==========

export function MetadataFilterPanel({
  metadataFilters,
  onBack,
  onApply,
  excludedClientIds = [],
}: MetadataFilterPanelProps) {
  const { pendingFilters, dispatch, clearFilters, applyFilters } =
    metadataFilters;

  const { data: clientsData, execute: executeClients } =
    useGraphQL<ClientsResponse>();

  useEffect(() => {
    executeClients(GET_CLIENTS_QUERY);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clientOptions: SearchableFilterOption[] = useMemo(() => {
    if (!clientsData?.clients) return [];
    return clientsData.clients
      .map((client) => ({
        value: client.id,
        label: client.name ?? "",
        disabled: excludedClientIds.includes(client.id) || client.is_restricted,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [clientsData, excludedClientIds]);

  const subcategoryOptions: SearchableFilterOption[] = useMemo(
    () =>
      APS_SUBCATEGORY_OPTIONS.map((opt) => ({
        value: opt,
        label: opt,
      })),
    []
  );

  const topicOptions: SearchableFilterOption[] = useMemo(
    () =>
      TOPIC_VALUES.map((t) => ({
        value: t,
        label: t,
      })),
    []
  );

  const handleApply = () => {
    applyFilters();
    onApply();
  };

  const handleClear = () => {
    clearFilters();
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Section 1: Inline filter chips */}
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Filter by metadata</h3>
          <div className="space-y-3">
            <FilterChips
              label="Category"
              options={APS_CATEGORY_OPTIONS}
              value={
                pendingFilters.apsCategory.length === 1
                  ? pendingFilters.apsCategory[0]
                  : null
              }
              onChange={(val) =>
                dispatch({
                  type: "setApsCategory",
                  values: val ? [val] : [],
                })
              }
            />
            <FilterChips
              label="Audience"
              options={AUDIENCE_VALUES}
              value={pendingFilters.audience}
              onChange={(val) =>
                dispatch({
                  type: "setAudience",
                  value: val as typeof pendingFilters.audience,
                })
              }
            />
            <FilterChips
              label="NOC Status"
              options={NOC_STATUS_VALUES}
              value={pendingFilters.nocStatus}
              onChange={(val) =>
                dispatch({
                  type: "setNocStatus",
                  value: val as typeof pendingFilters.nocStatus,
                })
              }
            />
            <FilterChips
              label="External Review"
              options={EXTERNAL_REVIEW_VALUES}
              value={pendingFilters.externalReview}
              onChange={(val) =>
                dispatch({
                  type: "setExternalReview",
                  value: val as typeof pendingFilters.externalReview,
                })
              }
            />
            <FilterChips
              label="Promotion Type"
              options={PROMOTION_TYPE_VALUES}
              value={pendingFilters.promotionType}
              onChange={(val) =>
                dispatch({
                  type: "setPromotionType",
                  value: val as typeof pendingFilters.promotionType,
                })
              }
            />
          </div>
        </div>

        {/* Section 2: Searchable multi-selects */}
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">
            Searchable filters
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <SearchableFilter
              label="Client"
              options={clientOptions}
              values={pendingFilters.clientIds}
              onChange={(vals) =>
                dispatch({ type: "setClientIds", values: vals })
              }
            />
            <SearchableFilter
              label="Sub-category"
              options={subcategoryOptions}
              values={pendingFilters.apsSubcategory}
              onChange={(vals) =>
                dispatch({ type: "setApsSubcategory", values: vals })
              }
            />
            <SearchableFilter
              label="Topic"
              options={topicOptions}
              values={pendingFilters.topic}
              onChange={(vals) =>
                dispatch({
                  type: "setTopic",
                  values: vals as typeof pendingFilters.topic,
                })
              }
            />
          </div>
        </div>

        {/* Section 3: Year inputs */}
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Date filters</h3>
          <div className="flex gap-6">
            <YearInput
              label="Start Year (from)"
              value={pendingFilters.startYear}
              onChange={(val) =>
                dispatch({ type: "setStartYear", value: val })
              }
            />
            <YearInput
              label="Approval Year (from)"
              value={pendingFilters.approvalYear}
              onChange={(val) =>
                dispatch({ type: "setApprovalYear", value: val })
              }
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
        <button
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50"
        >
          Back
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={handleClear}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-full hover:bg-orange-600"
          >
            Clear Filters
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 text-sm font-medium text-white bg-maven-teal-500 rounded-full hover:bg-maven-teal-700"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}
